import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const BRAND_LABEL = { bb: "B&B", betos: "Betos" };

export const SyncCustomersDialog = ({ brand, open, onOpenChange }) => {
  const { t } = useT();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["qbo-sync-preview", brand],
    queryFn: async () => (await api.post(`/qbo/connections/${brand}/sync-customers`)).data,
    enabled: open && !!brand,
  });

  const confirmMut = useMutation({
    mutationFn: (payload) => api.post(`/qbo/connections/${brand}/sync-customers/confirm`, payload),
    onSuccess: () => {
      toast.success(t("settings.accounting.sync_confirmed"));
      qc.invalidateQueries({ queryKey: ["qbo-sync-preview", brand] });
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const yaMapeados = data?.ya_mapeados || [];
  const candidatos = data?.candidatos || [];
  const sinCorrespondencia = data?.sin_correspondencia || [];
  const sinNovedad = !isLoading && data && !candidatos.length && !sinCorrespondencia.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`qbo-sync-dialog-${brand}`} className="max-w-2xl rounded-[10px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {t("settings.accounting.sync_dialog_title", { brand: BRAND_LABEL[brand] || brand })}
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <p className="py-6 text-center text-[13px] text-ink-tertiary">{t("app.loading")}</p>
        ) : (
          <div className="max-h-[60vh] space-y-5 overflow-y-auto">
            {sinNovedad && (
              <p data-testid="qbo-sync-no-news" className="text-[13px] text-ink-secondary">
                {t("settings.accounting.sync_nothing_new", { n: data?.remotos_total || 0 })}
              </p>
            )}
            {!!candidatos.length && (
              <div>
                <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-tertiary">
                  {t("settings.accounting.sync_candidates")}
                </p>
                <ul className="space-y-1.5" data-testid="qbo-sync-candidates">
                  {candidatos.map((c) => (
                    <li key={`${c.entity_tipo}-${c.entity_id}`} data-testid={`qbo-sync-candidate-${c.entity_id}`}
                      className="flex items-center justify-between gap-3 border border-line px-3 py-2 text-[13px]">
                      <div>
                        <p className="text-ink">{c.entity_nombre} <span className="text-ink-tertiary">→</span> {c.qbo_nombre}</p>
                        <p className="text-[11px] text-ink-tertiary">{t("settings.accounting.sync_score", { n: c.puntaje })}</p>
                      </div>
                      <Button
                        data-testid={`qbo-sync-confirm-${c.entity_id}`}
                        size="sm" disabled={confirmMut.isPending}
                        onClick={() => confirmMut.mutate({
                          entity_tipo: c.entity_tipo, entity_id: c.entity_id, qbo_customer_id: c.qbo_customer_id,
                        })}
                        className="h-7 shrink-0 rounded-control bg-brand text-[12px] text-paper hover:bg-brand-hover"
                      >
                        {t("settings.accounting.sync_confirm_row")}
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!!sinCorrespondencia.length && (
              <div>
                <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-tertiary">
                  {t("settings.accounting.sync_unmatched")}
                </p>
                <ul className="space-y-1" data-testid="qbo-sync-unmatched">
                  {sinCorrespondencia.map((r) => (
                    <li key={r.qbo_customer_id} className="text-[13px] text-ink-secondary">{r.nombre}</li>
                  ))}
                </ul>
              </div>
            )}
            {!!yaMapeados.length && (
              <div>
                <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-tertiary">
                  {t("settings.accounting.sync_already_mapped")} ({yaMapeados.length})
                </p>
                <ul className="space-y-1" data-testid="qbo-sync-mapped">
                  {yaMapeados.map((m) => (
                    <li key={`${m.entity_tipo}-${m.entity_id}`} className="text-[13px] text-ink-tertiary">
                      {m.entity_nombre} → {m.qbo_nombre}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 rounded-control text-[13px]">
            {t("app.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
